import { useSearchParams } from 'react-router-dom';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import { useAsync } from '../lib/useAsync';
import { mediaApi } from '../api/resources';
import { cx } from '../lib/format';
import { SkeletonGrid, ErrorState, EmptyState } from '../components/ui';

const TABS = [
  { key: '', label: 'All' },
  { key: 'image', label: 'Photos' },
  { key: 'document', label: 'Documents' },
  { key: 'video', label: 'Videos' },
];

function formatBytes(bytes) {
  if (!bytes || Number.isNaN(Number(bytes))) return null;
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = Number(bytes);
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value < 10 && i > 0 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
}

function fileTypeLabel(mime) {
  if (!mime) return 'File';
  const map = {
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/vnd.ms-powerpoint': 'PPT',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
    'text/plain': 'TXT',
    'text/csv': 'CSV',
    'application/zip': 'ZIP',
  };
  if (map[mime]) return map[mime];
  const sub = mime.split('/')[1];
  return sub ? sub.toUpperCase().slice(0, 4) : 'File';
}

function DocIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="13" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="m16 9 5-3v12l-5-3V9Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function MediaCard({ item }) {
  const url = item.url;
  const title = item.alt_text || item.name || 'Resource';

  if (item.kind === 'image') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="card card-hover overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-maroon-400 focus-visible:ring-offset-2"
      >
        <div className="flex h-44 items-center justify-center overflow-hidden bg-surface-gray">
          <img
            src={url}
            alt={item.alt_text || ''}
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="p-4">
          <p className="truncate text-sm font-semibold text-ink">{title}</p>
          <p className="mt-0.5 text-xs text-ink-muted">Photo</p>
        </div>
      </a>
    );
  }

  const isVideo = item.kind === 'video';
  const typeLabel = isVideo ? 'Video' : fileTypeLabel(item.mime_type);
  const size = formatBytes(item.size);

  return (
    <div className="card card-hover flex h-full flex-col overflow-hidden">
      <div className="flex items-start gap-3 p-4">
        <span
          aria-hidden="true"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-maroon-50 text-maroon-700 ring-4 ring-gold-200/70"
        >
          {isVideo ? <VideoIcon /> : <DocIcon />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-semibold text-ink" title={title}>
            {title}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-ink-muted">
            <span className="pill-gold">{typeLabel}</span>
            {size && <span>{size}</span>}
          </p>
        </div>
      </div>
      <div className="mt-auto border-t border-cream-300 p-3">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-outline w-full px-4 py-2 text-sm"
        >
          {isVideo ? 'Open' : 'Download'}
        </a>
      </div>
    </div>
  );
}

export default function MediaCenter() {
  const [params, setParams] = useSearchParams();
  const kind = params.get('kind') || params.get('type') || '';

  const { data, loading, error, reload } = useAsync(
    () => mediaApi.list({ per_page: 24, kind: kind || undefined }),
    [kind]
  );

  const setKind = (next) => {
    const sp = new URLSearchParams(params);
    sp.delete('type');
    if (next) sp.set('kind', next);
    else sp.delete('kind');
    setParams(sp);
  };

  const emptyMeta =
    kind === 'document'
      ? { icon: '📄', title: 'No documents yet', message: 'Downloadable resources will appear here once published.' }
      : kind === 'video'
        ? { icon: '🎬', title: 'No videos yet', message: 'Video resources will appear here once published.' }
        : kind === 'image'
          ? { icon: '🖼', title: 'No photos yet', message: 'Photos will appear here once published.' }
          : { icon: '🖼', title: 'No media yet', message: 'Resources and photos will appear here.' };

  return (
    <>
      <Seo
        title="Media & Resources"
        description="Photos, documents, and downloadable resources from Zonta District 17 Area 1."
      />
      <PageHero
        eyebrow="Resource Library"
        title="Media & Resources"
        subtitle="Browse photos and download resources from our community."
        image="/img/community.jpg"
      />

      <section className="container-page py-12">
        <div className="mb-8 flex flex-wrap gap-2" role="tablist" aria-label="Filter media by type">
          {TABS.map((tab) => {
            const active = kind === tab.key;
            return (
              <button
                key={tab.key || 'all'}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setKind(tab.key)}
                className={cx(
                  'btn px-5 py-2 text-sm',
                  active
                    ? 'bg-maroon-700 text-white shadow-soft'
                    : 'border-2 border-cream-300 text-ink-soft hover:border-maroon-200 hover:bg-maroon-50'
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          // Loaded grid below is 1 -> 2 -> 3 -> 4 at `gap-4`; the 3-up default
          // was a column short from 1280px up and a step wide on the gap.
          <SkeletonGrid count={8} columns="1-2-3-4" gap={4} />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data?.items?.length ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.items.map((m) => (
              <MediaCard key={m.id} item={m} />
            ))}
          </div>
        ) : (
          <EmptyState icon={emptyMeta.icon} title={emptyMeta.title} message={emptyMeta.message} />
        )}
      </section>
    </>
  );
}
